import { Location, PlatformLocation } from "@angular/common";
import { Component, HostBinding, Inject, isDevMode, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { BrowserUtils, EventMessage, EventType, InteractionStatus } from "@azure/msal-browser";
import { AuthenticationResult } from "@azure/msal-common";
import { ConfirmationService } from "primeng/api";
import { Subject, takeUntil } from "rxjs";
import { filter } from "rxjs/operators";
import { AccountService } from "src/app/common/services/account.service";
import { AclService } from "src/app/common/services/acl.service";
import { FeatureTourService } from "src/app/common/services/feature-tour.service";
import { NotificationService } from "src/app/common/services/notification.service";
import { UserTrackingService } from "src/app/common/services/user-tracking.service";
import { VersionInterceptorService } from "src/app/common/services/version-interceptor.service";
import * as tours from "src/app/common/tours";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [NotificationService],
})
export class AppComponent implements OnInit, OnDestroy {
  isIframe = false;

  @HostBinding("class.loading")
  loading = true;

  tokenExpiration = "";

  isAuthenticated = false;
  authCheckComplete = false;

  private readonly destroySubject = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly router: Router,
    private readonly location: Location,
    private readonly platformLocation: PlatformLocation,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly accountService: AccountService,
    private readonly aclService: AclService,
    private readonly userTrackingService: UserTrackingService,
    private readonly confirmationService: ConfirmationService,
    private readonly versionInterceptorService: VersionInterceptorService,
    private readonly featureTourService: FeatureTourService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  ngOnInit(): void {
    // Register feature tours
    this.registerFeatureTours();

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.handleAuthState();
        void this.setAccountAndLanguage();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE || msg.eventType === EventType.LOGIN_FAILURE,
        ),
      )
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.handleAuthFailure();
      });

    // Used for storing and displaying token expiration
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS))
      .pipe(takeUntil(this.destroySubject))
      .subscribe((msg) => {
        ({ expiresOn: this.tokenExpiration } = msg.payload as { expiresOn: string });
        localStorage.setItem("tokenExpiration", this.tokenExpiration);
      });

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .pipe(takeUntil(this.destroySubject))
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async (msg) => {
        const payload = msg.payload as AuthenticationResult;
        const account = payload.account;
        if (account) {
          this.msalService.instance.setActiveAccount(account);
          const accountId = account.localAccountId;
          const username = account.name;
          const roles = account.idTokenClaims?.roles;
          if (username) {
            await this.accountService.updateLastLogin(accountId, roles?.[0]);
          }
        }
      });

    const currentPath = this.location.path();
    this.isIframe = BrowserUtils.isInIframe() && !window.opener && !currentPath.includes("logout");
    this.versionInterceptorService.versionChange.pipe(takeUntil(this.destroySubject)).subscribe(() => {
      this.confirmationService.confirm({
        header: $localize`:@@version_changed_popup_header:Nouvelle version de BakerStreet`,
        message: $localize`:@@version_changed_popup_body:Une nouvelle version de BakerStreet est disponible. Veuillez rafraîchir la page la page.`,
        acceptLabel: $localize`:@@version_changed_popup_accept_label:Rafraîchir`,
        rejectLabel: $localize`:@@version_changed_popup_reject_label:Ignorer jusqu'au prochain rafraîchissement`,
        accept: () => {
          location.reload();
        },
      });
    });
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.loading = false;
        window.scrollTo(0, 0);
      });
  }

  async setAccountAndLanguage(): Promise<void> {
    const accounts = this.msalService.instance.getAllAccounts();
    const accountExist = accounts.length > 0;
    if (accountExist) {
      this.accountService.setAccountInfo(accounts[0]!);
      await this.setLanguage();
    }
  }

  private handleAuthState(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    const tokenExpiration = localStorage.getItem("tokenExpiration");
    const isTokenValid = tokenExpiration ? new Date(tokenExpiration) > new Date() : false;

    this.isAuthenticated = accounts.length > 0 && isTokenValid;
    this.authCheckComplete = true;

    if (!this.isAuthenticated && accounts.length > 0) {
      // Token exists but is expired
      this.msalService.loginRedirect();
    }
  }

  private handleAuthFailure(): void {
    this.authCheckComplete = true;
    this.isAuthenticated = false;
    this.msalService.loginRedirect();
  }

  getUrlLang(): string | null {
    const url = this.platformLocation.href;
    if (url.includes("/de")) {
      return "de";
    }
    if (url.includes("/fr")) {
      return "fr";
    }
    if (url.includes("/it")) {
      return "it";
    }
    return null;
  }

  async getUserLanguage(): Promise<string> {
    if (await this.aclService.isAuthorized("GERMAN_LANGUAGE")) {
      return "de";
    }
    if (await this.aclService.isAuthorized("ITALIAN_LANGUAGE")) {
      return "it";
    }
    return "fr";
  }

  async setLanguage(): Promise<void> {
    if (isDevMode()) {
      return;
    }
    // Only redirect to default language if no language is set in the URL
    const url = this.platformLocation.href;
    const urlLang = this.getUrlLang();
    if (!urlLang) {
      const allSlashIndices = [...url.matchAll(/\//g)].map((match) => match.index);
      //if there is no third slash, returns
      if (allSlashIndices.length < 3) {
        return;
      }
      const thirdSlashIndex = allSlashIndices[2];
      const newUrl = `${url.slice(0, thirdSlashIndex)}/${await this.getUserLanguage()}${url.slice(thirdSlashIndex)}`;
      window.location.href = newUrl;
    }
  }

  /** Register all feature tours See frontend/src/app/common/tours/README */
  private registerFeatureTours(): void {
    this.featureTourService.registerToursFromModule(tours);
  }
}
